import type { Device } from 'src/types/Device';

type FormatMessage = (id: string, options?: { default: string }) => string;

export type DeviceStatusMap = Record<string, { online?: boolean | number } | undefined>;

export type DeviceRoomGroup = {
	id: string;
	roomName: string;
	devices: Device[];
};

export const MAIN_ENERGY_METER_KEY = 'main_energy_meter';

export const MAIN_ENERGY_METER_TYPES: string[] = [
	'SPEM-003CEBEU',
	'S3EM-003CXCEU63',
	'SPEM-003CEBEU63',
	'SPEM-003CEBEU400',
	'SPEM-003REBEU2000',
	'SPEM-002CEBEU50',
	'S3EM-002CXCEU',
	'S4EM-002CXCEU',
];

export const isDeviceOnline = (device: { id: string }, statusMap: DeviceStatusMap = {}): boolean => {
	const baseId = device.id.replace(/_\d+$/, '');
	const status = statusMap[baseId];

	return status?.online === true || status?.online === 1;
};

export const getDeviceDescription = (
	deviceType: string,
	online: boolean,
	formatMessage: FormatMessage,
): string => {
	if (online) return deviceType;

	return `${deviceType} · ${formatMessage('label_device_offline', { default: 'Device is offline' })}`;
};

const sortOnlineFirst = (devices: Device[], statusMap: DeviceStatusMap): Device[] =>
	[...devices].sort(
		(a, b) => Number(!isDeviceOnline(a, statusMap)) - Number(!isDeviceOnline(b, statusMap)),
	);

export const groupDevicesByRoom = (
	devicesToGroup: Device[],
	statusMap: DeviceStatusMap,
	resolveRoomName: (roomId: number | undefined) => string,
	idPrefix: string,
): DeviceRoomGroup[] => {
	const byRoom = new Map<string, Device[]>();

	devicesToGroup.forEach((device) => {
		const roomName = resolveRoomName(device.room_id);
		const bucket = byRoom.get(roomName);
		if (bucket) {
			bucket.push(device);
			return;
		}
		byRoom.set(roomName, [device]);
	});

	return [...byRoom.entries()]
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([roomName, groupedDevices], index) => ({
			id: `${idPrefix}-room-${index}`,
			roomName,
			devices: sortOnlineFirst(groupedDevices, statusMap),
		}));
};

export const groupEnergyMetersByRoom = (
	allDevices: Device[],
	meterTypes: string[],
	statusMap: DeviceStatusMap,
	resolveRoomName: (roomId: number | undefined) => string,
	idPrefix: string,
): DeviceRoomGroup[] =>
	groupDevicesByRoom(
		allDevices.filter((device) => meterTypes.includes(device.type)),
		statusMap,
		resolveRoomName,
		idPrefix,
	);
